import { type CuiLookupAddress, type CuiLookupCompany, CuiLookupError } from '@pcrm/fe/ui-kit';
import { z } from 'zod';
import { ApiError, apiGet } from '../../lib/api';

/**
 * Căutarea firmei la ANAF, dinspre client.
 *
 * **Aici stă apelul**, nu în componentă: clientul de API e al aplicației, iar
 * `libs/fe/ui-kit` e o bibliotecă — n-are voie să depindă de `apps/`. Componenta primește
 * funcția asta ca `onLookup` și atât. Vezi tichetul #52.
 *
 * Frontendul cheamă **backendul nostru**, nu ANAF: adresa serviciului extern (`ANAF_URL`)
 * e o variabilă de mediu a serverului, iar răspunsul brut nu trece niciodată granița.
 */

const addressSchema = z.object({
  street: z.string().nullable(),
  city: z.string().nullable(),
  county: z.string().nullable(),
  // Codul din lista închisă, pentru județul firmei emitente (#183). `null` = nerecunoscut.
  countyCode: z.string().nullable(),
  postalCode: z.string().nullable(),
});

/**
 * Schema e declarată aici, nu importată din `@pcrm/be/anaf`: `apps/crm` e `scope:fe`.
 * Aceeași alegere ca la `companies.api.ts` — granița se validează în ambele capete, iar un
 * câmp redenumit pe server pică la `parse`, nu ajunge `undefined` în formular.
 */
const anafCompanySchema = z.object({
  cui: z.string(),
  name: z.string(),
  tradeRegisterNo: z.string().nullable(),
  address: addressSchema,
  phone: z.string().nullable(),
  vatPayer: z.boolean(),
  vatOnCollection: z.boolean(),
  status: z.enum(['active', 'inactive', 'struck_off']),
  statusText: z.string().nullable(),
  checkedAt: z.string(),
});

/**
 * Statusul HTTP → codul pe care componenta îl arată.
 *
 * **Pe status, nu pe `code`:** controllerul trimite cele trei erori de domeniu ca
 * 422 / 404 / 503 (`apps/api/src/anaf/anaf.controller.ts`), iar excepțiile standard de
 * NestJS n-au `code` în corp. Traducerea asta e cunoaștere despre API-ul nostru, deci stă
 * în aplicație — componenta nu vede niciun status HTTP.
 */
const STATUS_CODES = {
  422: 'invalid_cui',
  404: 'not_found',
  503: 'unavailable',
} as const;

export function toLookupError(error: unknown): CuiLookupError {
  if (!(error instanceof ApiError)) {
    // Rețea căzută, DNS, `parse` picat pe un contract schimbat. Mesajul lui `Error` e
    // pentru dezvoltator, nu pentru operator, deci nu se arată — rămâne textul generic.
    return new CuiLookupError('unknown');
  }

  const code = STATUS_CODES[error.status as keyof typeof STATUS_CODES];

  // Restul (401 cu sesiunea expirată, 500, 502 de la proxy) pleacă cu mesajul serverului:
  // acolo el spune mai mult decât putem noi ghici din status.
  return code ? new CuiLookupError(code) : new CuiLookupError('unknown', error.message);
}

/** Primește CUI-ul deja normalizat de componentă; serverul îl validează oricum din nou. */
export function lookupCompanyByCui(cui: string): Promise<CuiLookupCompany> {
  return apiGet(`/anaf/companies/${encodeURIComponent(cui)}`, anafCompanySchema).catch(
    (error: unknown) => {
      throw toLookupError(error);
    },
  );
}

/**
 * Adresa ANAF → linia din formular.
 *
 * Formularul are **un** câmp de adresă, ANAF are patru. Codul poștal intră la sfârșit, ca
 * pe plic. Ce lipsește se sare: „București, , ROMÂNIA" e mai rău decât o adresă scurtă,
 * iar câmpurile chiar lipsesc — Vodafone vine fără cod poștal.
 */
export function formatAnafAddress(address: CuiLookupAddress): string {
  return [address.street, address.city, address.county, address.postalCode]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(', ');
}
